import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {
    isOrganisationActive,
    OrganisationDetail,
    OrganisationLinkedService
} from '@shared/api/models/query/organisation.model';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkedRecordsTabBase} from '@shared/components/linked-records/linked-records-tab.base';
import {LinkRecordsModalConfig} from '@shared/components/linked-records/link-records.modal';

/**
 * UNIVAPLAT-3697: Services tab of the organisation detail page.
 *
 * - Lists the services linked to the organisation, newest first. Link and unlink need
 *   MANAGE_ORGANISATION_SERVICES (general admins and the organisation's admins) and an active organisation.
 * - The API answers 422 on the linked lists of a deactivated or deleted organisation, so for those
 *   the tab shows a message and neither loads nor calls `linked=false`.
 * - The link popup lists the active services not yet linked (`linked=false`) and adds the selection
 *   with one `addServices` call. After a change the tab emits `changed`, so the page refreshes
 *   `linkedServices`, which drives the delete and deactivate guards.
 * - Backend owns the error text. Service data is never logged and is dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-services-tab',
    standalone: true,
    imports: [DatePipe, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './organisation-services-tab.component.html',
    styleUrl: './organisation-services-tab.component.scss'
})
export class OrganisationServicesTabComponent extends LinkedRecordsTabBase<OrganisationLinkedService> implements OnChanges {
    @Input() organisation!: OrganisationDetail;

    protected readonly entityLabel = 'Service';

    private readonly organisationsService = inject(OrganisationsService);
    private readonly authService = inject(AuthService);

    get isActive(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    get canWrite(): boolean {
        return this.isActive && this.hasPermission(Permissions.MANAGE_ORGANISATION_SERVICES);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['organisation']) {
            return;
        }
        const previous = changes['organisation'].previousValue as OrganisationDetail | undefined;
        const current = this.organisation;
        // Reload on first render, a different organisation, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isOrganisationActive(previous) !== isOrganisationActive(current)) {
            void this.load();
        }
    }

    rowId(row: OrganisationLinkedService): number {
        return row.id;
    }

    protected async fetch(): Promise<OrganisationLinkedService[]> {
        if (!this.organisation || !this.isActive) {
            return [];
        }
        const rows = await this.organisationsService.listServices(this.organisation.id);
        // Newest first; a missing date sorts last.
        return [...rows].sort((a, b) => Date.parse(b.addedOn ?? '') - Date.parse(a.addedOn ?? '') || 0);
    }

    protected async unlinkOne(row: OrganisationLinkedService): Promise<void> {
        await this.organisationsService.removeService(this.organisation.id, row.id);
    }

    protected linkModalConfig(): LinkRecordsModalConfig<OrganisationLinkedService> {
        const organisationId = this.organisation.id;
        return {
            heading: 'Link services',
            titleId: 'link-organisation-services-title',
            columns: [
                {header: 'Name', value: service => service.name},
                {header: 'Address', value: service => service.address},
                {header: 'City', value: service => service.city},
                {header: 'Postcode', value: service => service.postcode},
            ],
            loader: () => this.organisationsService.listServices(organisationId, {linked: false}),
            submitLabel: 'Add selected to organisation',
            successMessage: 'Services linked to organisation',
            emptyMessage: 'No services left to link',
            submit: async (ids: number[]) => {
                await this.organisationsService.addServices(organisationId, ids);
            },
        };
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }
}
